import { AdminAbility } from '@repo/sdk/backoffice-abilities'
import {
	SidebarGroup,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar
} from '@repo/ui/sidebar'
import { useSuspenseQuery } from '@tanstack/react-query'
import { Link, type LinkProps } from '@tanstack/react-router'
import {
	Building2,
	CalendarClock,
	Download,
	FileText,
	Home,
	LifeBuoy,
	Mail,
	Send,
	ShieldCheck,
	Users
} from 'lucide-react'
import type { ReactElement } from 'react'
import { useTranslation } from '@/lib/i18n'
import { Can } from '@/modules/permissions/components/can'
import { adminSessionQueryOptions } from '@/query/query-options'

interface SidebarLink {
	icon: ReactElement
	label: string
	to: LinkProps['to']
	params?: LinkProps['params']
	/**
	 * When `true`, the link is only active when the URL matches `to` exactly.
	 * Defaults to `false` — child routes keep the parent link active (e.g.
	 * "/users/123" highlights "Users").
	 */
	exact?: boolean
	/** Required permissions to see the link. Empty/undefined = always visible. */
	permissions?: AdminAbility[]
}

function AdminNavGroups() {
	const { t } = useTranslation()
	const { isMobile, setOpenMobile } = useSidebar()
	const session = useSuspenseQuery(adminSessionQueryOptions()).data
	const isManager =
		session.sessionType === 'authenticated' &&
		session.admin.role === 'facility_manager'

	const handleNavigate = () => {
		if (isMobile) {
			setOpenMobile(false)
		}
	}

	const links: SidebarLink[] = [
		// The home is the general admin's console: a facility manager starts
		// from its residences, then the calls made from them.
		...(isManager
			? []
			: [
					{
						icon: <Home />,
						label: t('admin:home.page_title'),
						to: '/',
						exact: true
					} satisfies SidebarLink
				]),
		{
			icon: <Building2 />,
			label: t('admin-call:structures.page_title'),
			to: '/strutture',
			permissions: [AdminAbility.CallManage]
		},
		{
			icon: <FileText />,
			label: t('admin-call:calls.page_title'),
			to: '/bando',
			permissions: [AdminAbility.CallManage]
		},
		{
			icon: <Download />,
			label: t('admin-exports:manager.page_title'),
			to: '/exports',
			exact: true,
			permissions: [AdminAbility.CallManage]
		},
		{
			icon: <Building2 />,
			label: t('admin-residences:page_title'),
			to: '/residences',
			permissions: [AdminAbility.ResidencesManage]
		},
		{
			icon: <CalendarClock />,
			label: t('admin-monitoring:page_title'),
			to: '/bandi',
			permissions: [AdminAbility.CallMonitor]
		},
		{
			icon: <Send />,
			label: t('admin-sending:page_title'),
			to: '/invii',
			permissions: [AdminAbility.SendingMonitor]
		},
		{
			icon: <LifeBuoy />,
			label: t('admin-support:page_title'),
			to: '/assistenza',
			permissions: [AdminAbility.SupportManage]
		},
		{
			icon: <Mail />,
			label: t('admin-email-texts:page_title'),
			to: '/email-texts',
			permissions: [AdminAbility.EmailTextsManage]
		},
		{
			icon: <Users />,
			label: t('admin-users:page_title'),
			to: '/users',
			permissions: [AdminAbility.UsersManage]
		},
		{
			icon: <ShieldCheck />,
			label: t('admin-admins:page_title'),
			to: '/admins',
			permissions: [AdminAbility.AdminsManage]
		}
	]

	return (
		<SidebarGroup>
			<SidebarMenu>
				{links.map((link) => {
					const item = (
						<SidebarMenuItem key={link.to}>
							<SidebarMenuButton
								className='data-[status=active]:bg-sidebar-accent data-[status=active]:font-medium data-[status=active]:text-sidebar-accent-foreground'
								render={
									<Link
										activeOptions={{
											exact: link.exact ?? false
										}}
										onClick={handleNavigate}
										params={link.params}
										to={link.to}
									/>
								}
								tooltip={link.label}
							>
								{link.icon}
								<span>{link.label}</span>
							</SidebarMenuButton>
						</SidebarMenuItem>
					)

					if (link.permissions && link.permissions.length > 0) {
						return (
							<Can key={link.to} permissions={link.permissions}>
								{item}
							</Can>
						)
					}

					return item
				})}
			</SidebarMenu>
		</SidebarGroup>
	)
}

export { AdminNavGroups }
